import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User, Partner } from '../types/api';
import { partnerApiService } from '../services/api';
import { EVENT_UNAUTHORIZED } from '../services/apiErrors';
import { clearDraft, clearPendingPhone, getPendingPhone, setPendingPhone } from '../services/registrationDraft';

interface AuthContextType {
  user: User | null;
  partner: Partner | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * Номер незаконченной регистрации: код подтверждён, токен есть, анкета не
   * отправлена. Пока он не `null`, кабинет закрыт — бэк отвечает на его
   * запросы `409 registration_incomplete`.
   */
  registrationPhone: string | null;
  login: (user: User, partner: Partner, token: string) => void;
  logout: () => void;
  /** Шаг 2 регистрации прошёл: токен есть, заведения ещё нет. */
  beginRegistration: (user: User, token: string, phone: string) => void;
  /** Кабинет ответил `registration_incomplete` — вернуть человека в анкету. */
  requireRegistration: () => void;
  /**
   * Анкета отправлена: заведение создано, кабинет открывается.
   *
   * `null` — когда сервер ответил `already_registered` на повторную отправку:
   * заведение есть, но его данных у нас нет, и выдумывать их нельзя. Профиль
   * подтянется сам.
   */
  completeRegistration: (partner: Partner | null) => void;
  updatePartner: (partner: Partner) => void;
  loadPartnerProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [partner, setPartner] = useState<Partner | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [registrationPhone, setRegistrationPhone] = useState<string | null>(null);

  const loadPartnerProfile = useCallback(async () => {
    if (!isAuthenticated) return;

    console.log('Загружаем профиль партнера');
    setIsLoading(true);
    try {
      const response = await partnerApiService.getProfile();
      if (response.data) {
        setPartner(response.data);
        localStorage.setItem('partner', JSON.stringify(response.data));
      }
    } catch (error) {
      console.error('Error loading partner profile:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const logout = useCallback(() => {
    setUser(null);
    setPartner(null);
    setIsAuthenticated(false);
    setRegistrationPhone(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('partner');
    clearPendingPhone();
  }, []);

  // Обработчик события unauthorized
  useEffect(() => {
    const handleUnauthorized = () => {
      console.log('Получено событие unauthorized, выполняем выход');
      logout();
    };

    window.addEventListener(EVENT_UNAUTHORIZED, handleUnauthorized);
    return () => {
      window.removeEventListener(EVENT_UNAUTHORIZED, handleUnauthorized);
    };
  }, [logout]);

  useEffect(() => {
    // Проверяем сохраненные данные при загрузке
    const token = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('user');
    const savedPartner = localStorage.getItem('partner');

    if (token && savedUser && savedPartner) {
      try {
        setUser(JSON.parse(savedUser));
        setPartner(JSON.parse(savedPartner));
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing saved auth data:', error);
        logout();
      }
      return;
    }

    // Токен есть, заведения нет — человек закрыл вкладку на анкете.
    // Возвращаем его туда, где остановился: введённое лежит в черновике.
    const pendingPhone = getPendingPhone();
    if (token && pendingPhone) {
      if (savedUser) {
        try {
          setUser(JSON.parse(savedUser));
        } catch (error) {
          console.error('Error parsing saved auth data:', error);
        }
      }
      setRegistrationPhone(pendingPhone);
    }
  }, [logout]);

  // Отдельный useEffect для загрузки профиля после восстановления сессии
  useEffect(() => {
    if (isAuthenticated && user && !partner) {
      loadPartnerProfile();
    }
  }, [isAuthenticated, user, partner, loadPartnerProfile]);

  // Профиль перезагружаем по событиям (visibilitychange / focus / 5-min interval)
  // намеренно не делаем — это перезатирало активные формы и создавало ощущение
  // «сайт сам обновляется». Свежие данные подтянутся при логине, после сохранения
  // и при навигации между маршрутами (см. App.tsx).

  const login = useCallback((userData: User, partnerData: Partner, token: string) => {
    setUser(userData);
    setPartner(partnerData);
    setIsAuthenticated(true);
    setRegistrationPhone(null);
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('partner', JSON.stringify(partnerData));
    clearPendingPhone();
  }, []);

  /**
   * Номер подтверждён, анкета впереди.
   *
   * Токен сохраняем сразу — анкета уходит уже под ним. `isAuthenticated`
   * при этом остаётся `false`: заведения ещё нет, и кабинет открывать нечем.
   */
  const beginRegistration = useCallback((userData: User, token: string, phone: string) => {
    setUser(userData);
    setPartner(null);
    setIsAuthenticated(false);
    setRegistrationPhone(phone);
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.removeItem('partner');
    setPendingPhone(phone);
  }, []);

  const requireRegistration = useCallback(() => {
    setPartner(null);
    setIsAuthenticated(false);
    localStorage.removeItem('partner');

    // Номера может не быть: такое бывает, если вкладку открыли с живым
    // токеном, но без черновика. Тогда ведём регистрацию сначала — в анкету
    // вести некуда, она привязана к телефону.
    setRegistrationPhone((current) => current ?? getPendingPhone() ?? '');
  }, []);

  const completeRegistration = useCallback((partnerData: Partner | null) => {
    setIsAuthenticated(true);
    if (partnerData) {
      setPartner(partnerData);
      localStorage.setItem('partner', JSON.stringify(partnerData));
    }
    setRegistrationPhone((phone) => {
      if (phone) clearDraft(phone);
      return null;
    });
    clearPendingPhone();
  }, []);

  const updatePartner = useCallback((partnerData: Partner) => {
    setPartner(partnerData);
    localStorage.setItem('partner', JSON.stringify(partnerData));
  }, []);

  const value: AuthContextType = {
    user,
    partner,
    isAuthenticated,
    isLoading,
    registrationPhone,
    login,
    logout,
    beginRegistration,
    requireRegistration,
    completeRegistration,
    updatePartner,
    loadPartnerProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
